import { IconButton } from '@atlaskit/button/new';
import SearchIcon from '@atlaskit/icon/core/search';
import { useIsReducedMotion } from '@atlaskit/motion';
import { token } from '@atlaskit/tokens';
import { messages } from '@databases/intl/messages';
import usePanel from '@databases/panel/usePanel';
import {
  type KeyboardShortcut,
  useDebouncedCallback,
  useKeyboardShortcut,
} from '@databases/react';
import { SearchBar } from '@databases/search-bar/SearchBar';
import { container, getService } from '@databases/service-container';
import { type DatabaseViewSettings } from '@databases/types/database/view';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';
import styled, { css } from 'styled-components';
import { useSnapshot } from 'valtio';

import { isApple } from '../../../utils';

export interface QuickSearchProps {
  value?: DatabaseViewSettings['search'];
  onChange: (value: DatabaseViewSettings['search']) => void;
  tooltip: React.FC<{ children: React.ReactNode }>;
  /**
   * Optional callback that fires when the search field expands or collapses.
   *
   * @param expanded - true when expanded, false when collapsed
   */
  onExpandedChange?: (expanded: boolean) => void;
  useCompactWidth?: boolean;
  isExpanded?: boolean;
}

/**
 * Duration of the search field expand/collapse animation in milliseconds.
 * Used to synchronize visibility timing in parent components.
 * This is ignored when prefers-reduced-motion is enabled.
 */
export const SEARCH_TRANSITION_DURATION_MS = 300;

const COMPACT_WIDTH = 240;
const DEFAULT_WIDTH = 382;

export const SearchFieldTransition = styled.div<{
  expanded: boolean;
  useCompactWidth: boolean;
}>`
  transition: width ${SEARCH_TRANSITION_DURATION_MS}ms,
    background ${SEARCH_TRANSITION_DURATION_MS}ms;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  ${(props) =>
    props.expanded
      ? css`
          width: ${props.useCompactWidth ? COMPACT_WIDTH : DEFAULT_WIDTH}px;
        `
      : css`
          width: 32px;
        `}
`;

const findShortcut: KeyboardShortcut = {
  modifiers: [isApple ? 'metaKey' : 'ctrlKey'],
  code: 'KeyF',
};
const blurShortcut: KeyboardShortcut = { code: 'Escape' };

const QuickSearch: FunctionComponent<QuickSearchProps> = ({
  onChange: onChangeProp,
  value: valueProp,
  tooltip: IconTooltip,
  onExpandedChange,
  useCompactWidth = false,
  isExpanded: isExpandedProp,
}) => {
  const [value, setLocalValue] = useState(valueProp);
  const debouncedOnChanged = useDebouncedCallback(onChangeProp, 500);
  const isEmpty = !value;

  /**
   * The local value should be updated immediately, but the callback
   * to trigger the search should be debounced.
   */
  const updateValue = useCallback(
    (value?: string) => {
      setLocalValue(value);
      debouncedOnChanged(value);
    },
    [setLocalValue, debouncedOnChanged]
  );

  const searchFieldRef = useRef<HTMLInputElement>(null);
  const [internalExpanded, setInternalExpanded] = useState(!isEmpty);
  const expanded = isExpandedProp !== undefined ? isExpandedProp : internalExpanded;
  const isReducedMotion = useIsReducedMotion();
  const expandedChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Notify about expansion state changes with intelligent timing:
   * - When expanding: Fire immediately (hide labels right away)
   * - When collapsing: Delay until after animation (show labels after transition)
   * This prevents horizontal scrollbar flicker during collapse animation.
   */
  const setExpanded = useCallback(
    (value: boolean) => {
      setInternalExpanded(value);

      if (expandedChangeTimeoutRef.current !== null) {
        clearTimeout(expandedChangeTimeoutRef.current);
        expandedChangeTimeoutRef.current = null;
      }

      if (value) {
        onExpandedChange?.(true);
      } else {
        const delay = isReducedMotion ? 0 : SEARCH_TRANSITION_DURATION_MS + 20;
        expandedChangeTimeoutRef.current = setTimeout(() => {
          onExpandedChange?.(false);
          expandedChangeTimeoutRef.current = null;
        }, delay);
      }
    },
    [onExpandedChange, isReducedMotion]
  );

  useEffect(() => {
    // Only sync initial expansion state when uncontrolled — in controlled mode
    // the parent already owns this state and firing here would cause a redundant update.
    if (internalExpanded && isExpandedProp === undefined) {
      onExpandedChange?.(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Sync local search field state with external changes to current
   * view settings (changing views, etc)
   */
  useEffect(() => {
    setLocalValue(valueProp);
    if (!valueProp) {
      setExpanded(false);
    }
  }, [valueProp, setExpanded]);

  const { formatMessage } = useIntl();

  const selectionsActions = getService('SelectionsActions');
  const { hidePanel } = usePanel();
  const panelManager = container.resolve('PanelManager');
  const { panel: activePanel } = useSnapshot(panelManager.state);
  const isPanelActive = activePanel !== null;

  useEffect(() => {
    if (useCompactWidth && isPanelActive) {
      setExpanded(false);
    }
  }, [useCompactWidth, isPanelActive, setExpanded]);

  const handleExpandField = useCallback(() => {
    selectionsActions.deselectAll();
    if (useCompactWidth) {
      hidePanel();
    }
    setExpanded(true);
  }, [selectionsActions, hidePanel, useCompactWidth, setExpanded]);

  const handleFocus = useCallback(() => {
    searchFieldRef.current?.select();
  }, []);

  const handleBlur = useCallback(() => {
    isEmpty && setExpanded(false);
  }, [isEmpty, setExpanded]);

  const handleEscape = useCallback(() => {
    searchFieldRef.current?.blur();
  }, []);

  const handleClear = useCallback(() => {
    updateValue(undefined);
    searchFieldRef.current?.focus();
  }, [updateValue]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!expanded) {
        handleExpandField();
      }

      const value = e.target.value;

      updateValue(value);
    },
    [expanded, handleExpandField, updateValue]
  );

  useKeyboardShortcut(expanded ? blurShortcut : undefined, handleEscape);
  useKeyboardShortcut(!expanded ? findShortcut : undefined, handleExpandField);
  useKeyboardShortcut(expanded && !isEmpty ? findShortcut : undefined, handleFocus);

  return (
    <SearchFieldTransition expanded={expanded} useCompactWidth={useCompactWidth}>
      {expanded ? (
        <SearchBar
          searchFieldRef={searchFieldRef}
          value={value ?? ''}
          handleOnChange={handleChange}
          handleOnClear={handleClear}
          placeholder={formatMessage(messages['quick-search.search-database-text'])}
          autoComplete="off"
          autoFocus
          onBlur={handleBlur}
          isCompact={useCompactWidth}
        />
      ) : (
        <IconTooltip>
          <IconButton
            icon={(iconProps) => (
              <SearchIcon
                {...iconProps}
                size="medium"
                color={!isEmpty ? token('color.icon.selected') : undefined}
              />
            )}
            label={formatMessage(messages['quick-search.search-database-text'])}
            onClick={handleExpandField}
            appearance="subtle"
            isSelected={!isEmpty}
            spacing="default"
          />
        </IconTooltip>
      )}
    </SearchFieldTransition>
  );
};

export default QuickSearch;
